/**
 * Eén gedeelde stapel voor alle lades (Drawer). Zonder deze stapel regelde elke lade zelf de
 * scroll-lock, `inert` en Escape. Gingen er twee tegelijk open of dicht (huidadvies sluit terwijl
 * de winkelmand opengaat), dan haalde de ene de lock van de andere weg of maakte hem onbereikbaar.
 *
 * Regels:
 * - Alleen de bovenste lade reageert op Escape en Tab.
 * - Alles naast de bovenste lade is `inert`, ook een lade die nog aan het wegschuiven is.
 * - De pagina scrollt pas weer als de laatste lade dicht is.
 * - Een lade die later opengaat, ligt altijd boven een eerdere (`depth`).
 */

export type ModalEntry = {
  root: () => HTMLElement | null
  onKey: (e: KeyboardEvent) => void
}

const stack: ModalEntry[] = []
const inerted = new Set<HTMLElement>()
let bodyLock: { overflow: string; paddingRight: string } | null = null

function onDocumentKey(e: KeyboardEvent) {
  stack[stack.length - 1]?.onKey(e)
}

function sync() {
  inerted.forEach((el) => el.removeAttribute('inert'))
  inerted.clear()
  const top = stack[stack.length - 1]

  if (!top) {
    if (bodyLock) {
      document.body.style.overflow = bodyLock.overflow
      document.body.style.paddingRight = bodyLock.paddingRight
      bodyLock = null
    }
    document.removeEventListener('keydown', onDocumentKey)
    return
  }

  if (!bodyLock) {
    // Scrollbalk-breedte opvangen, zodat de pagina achter de lade niet verspringt.
    const scrollbar = window.innerWidth - document.documentElement.clientWidth
    bodyLock = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight }
    document.body.style.overflow = 'hidden'
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`
    document.addEventListener('keydown', onDocumentKey)
  }

  const topRoot = top.root()
  Array.from(document.body.children).forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    if (el === topRoot || (topRoot && el.contains(topRoot))) return
    // Meldingen (sonner) blijven bereikbaar, zodat "Ongedaan maken" werkt.
    if (el.hasAttribute('data-toaster-root')) return
    // Al inert door iets anders: niet van ons, dus ook niet door ons weghalen.
    if (el.hasAttribute('inert')) return
    el.setAttribute('inert', '')
    inerted.add(el)
  })
}

/** Lade erbij (bij openen). Geeft de diepte terug: 0 = onderste. */
export function pushModal(entry: ModalEntry): number {
  stack.push(entry)
  sync()
  return stack.length - 1
}

/** Lade eraf (bij sluiten), ook als hij niet bovenaan ligt. */
export function removeModal(entry: ModalEntry): void {
  const i = stack.indexOf(entry)
  if (i >= 0) stack.splice(i, 1)
  sync()
}

/** Voor tests: hoeveel lades er open zijn. */
export const openModalCount = () => stack.length
